import React, { useState, useEffect, useMemo } from 'react';
import { ArrowRight, Search, FolderKanban, FileText, Check, Loader2, Lock, MessageSquare, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { listLookupChats, type SavedLookupChat } from '../../lookupApi';
import HomeModuleCards from './HomeModuleCards';
import api from '../../apiService';
import { useAuthActions } from '../../context/AuthContext';
import { useShellUiActions } from '../../context/ShellUiContext';
import type { ProjectSummary, FeedFacets } from '../../apiService';
import FeedFilter from './FeedFilter';
import DialogShell from '../ui/DialogShell';
import { formatDateTime } from '../../utils/formatDateTime';
import { isChatLocked, isMcpOnlyCustomer } from '../../utils/moduleAccess';

// Feed filter persistence (v1: per-browser). Stored as arrays of "hidden"
// source/type values; default empty = show everything, so new regulators
// appear automatically. Server-side per-user/tenant persistence is the upgrade.
const loadHiddenSet = (key: string): Set<string> => {
    try {
        const raw = localStorage.getItem(key);
        return new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
        return new Set();
    }
};
const persistHiddenSet = (key: string, value: Set<string>) => {
    try {
        localStorage.setItem(key, JSON.stringify(Array.from(value)));
    } catch {
        /* ignore quota / private-mode errors */
    }
};
const HIDDEN_SOURCES_KEY = 'bark.feed.hiddenSources';
const HIDDEN_TYPES_KEY = 'bark.feed.hiddenTypes';
const HIDDEN_CATEGORIES_KEY = 'bark.feed.hiddenCategories';

// An item passes the category filter if it's uncategorised, or at least one of
// its themes is still visible. (Pure helper so faceted memos can reuse it.)
// Cache the last real feed we received, so when the endpoint is briefly
// unavailable (e.g. a redeploy) we fall back to the last REAL items (which have
// working links) instead of the hardcoded seed. Seed is only the last resort
// for a brand-new browser that has never loaded a real feed.
const FEED_CACHE_KEY = 'bark.feed.cache';
const loadCachedFeed = (): FeedItem[] | null => {
    try {
        const raw = localStorage.getItem(FEED_CACHE_KEY);
        const arr = raw ? JSON.parse(raw) : null;
        return Array.isArray(arr) && arr.length ? (arr as FeedItem[]) : null;
    } catch {
        return null;
    }
};
const saveCachedFeed = (items: FeedItem[]) => {
    try {
        localStorage.setItem(FEED_CACHE_KEY, JSON.stringify(items));
    } catch {
        /* ignore quota / private-mode errors */
    }
};

const passesCategoryFilter = (cats: string[], hidden: Set<string>) =>
    cats.length === 0 || cats.some((c) => !hidden.has(c));

// Items the categoriser left without a theme become "Other" — but only when
// tagging actually worked (at least one item got a real tag). When nothing is
// tagged (tagging off/broken) we leave items uncategorised so the feed shows
// everything plain rather than labelling everything "Other".
const OTHER_CATEGORY = 'Other';
const effectiveCategories = (cats: string[], taggingWorked: boolean): string[] =>
    cats.length > 0 ? cats : (taggingWorked ? [OTHER_CATEGORY] : []);

// Start-page content below the Research / Build entry buttons: "Your
// work", the module cards, and the regulator feed. This used to be
// HomeChatAgent — the home chat composer and its side panel were removed
// 2026-09-10 (the start page sends people into a workspace instead of a
// chat); the feed's per-row "Ask BARK" now hands the question to
// `onAskBark` (LandingRoute opens a research project with it) rather than
// seeding a composer that no longer exists.
interface HomeOverviewProps {
    /** Feed row "Ask BARK": open a research session seeded with `query`.
     *  Rejects when the project could not be created — the row shows the
     *  failure inline. */
    onAskBark: (query: string) => Promise<void> | void;
    authToken?: string;
    // BAR-298: project list shown in the right column ("Your work").
    projectList: ProjectSummary[];
    projectsLoading?: boolean;
    /** The project list failed to load: say so, with Try again, rather than
     *  showing the empty state to someone who may have projects. */
    projectsError?: boolean;
    onRetryProjects?: () => void;
    onProjectSelect: (projectId: string) => void;
    onNavigate: (
        destination: 'projects' | 'new-project' | 'quick-research' | 'settings',
        projectType?: 'research' | 'build',
    ) => void;
}

// Seed feed — the fallback shown when the live endpoint returns nothing
// (cold cache, unverified source URLs, or offline). The live feed comes
// from GET /api/home-agent/regulatory-feed (orchestrator → external_
// document_service), which ingests regulator RSS (EBA, FI, EUR-Lex) and
// generates the per-item "Ask: …" question. Once the live feed has items
// it fully replaces this seed.
interface FeedItem {
    source: string;
    date: string;        // YYYY-MM-DD
    type: string;
    title: string;
    summary?: string;    // one-line abstract shown under the title
    link?: string;       // canonical URL — clicking the card opens this
    regulationId?: string;
    categories?: string[]; // LLM-assigned regulatory themes (filterable)
    /** AI-generated "Ask BARK" question (empty until enriched). */
    question?: string;
}

// No fabricated fallback items. Inventing regulatory publications — even as a
// placeholder — is a compliance risk for a RegTech product: a user could mistake
// a fake "Final draft RTS…" for a real one. When there is no cached/live feed yet
// the card renders a neutral loading state instead (see the empty state below).
const SEED_FEED: FeedItem[] = [];

const COLUMN_VISIBLE = 6;

// An empty list's start button: the round "+" of Chat's "New chat" row,
// centered, with its label under it.
const emptyStartClass =
    'flex flex-col items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-semibold text-fg hover:bg-bg-warm focus-visible:shadow-bark-focus';
const emptyStartCircle =
    'inline-flex items-center justify-center w-10 h-10 rounded-full bg-surface border border-border-default text-fg-secondary';
// "Your chats" and "Your projects" each show this many, newest first.
const HOME_LIST_SIZE = 5;

// Bucket a YYYY-MM-DD into a coarse recency label for in-feed dividers.
// Buckets only render when the bucket changes between adjacent items,
// so an all-this-week column shows no divider noise — the divider only
// fires when there's a real transition (Yesterday → This week, etc.).
const DAY_MS = 24 * 60 * 60 * 1000;
function dateBucket(dateStr: string, today: Date): string {
    const date = new Date(dateStr + 'T00:00:00');
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const diffDays = Math.floor((todayStart.getTime() - date.getTime()) / DAY_MS);
    if (diffDays <= 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return 'This week';
    if (diffDays < 14) return 'Last week';
    return 'Earlier';
}

// Compact relative-time string for the "Your work" project rows.
function relativeTime(iso: string, today: Date): string {
    const date = new Date(iso);
    const diffMin = Math.floor((today.getTime() - date.getTime()) / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return formatDateTime(date, { context: 'date' });
}

type FeedRow =
    | { kind: 'divider'; label: string }
    | { kind: 'item'; item: FeedItem };

function withDividers(items: FeedItem[], today: Date): FeedRow[] {
    const out: FeedRow[] = [];
    let last: string | null = null;
    for (const it of items) {
        const b = dateBucket(it.date, today);
        // Dividers only render at TRANSITIONS — the first bucket is
        // implied by the section header above and doesn't need a label.
        if (last !== null && b !== last) {
            out.push({ kind: 'divider', label: b });
        }
        last = b;
        out.push({ kind: 'item', item: it });
    }
    return out;
}

// NOTE: the "In the corpus" start-page section was removed here — corpus (UI +
// data pipeline) is owned by BAR-301 and will be rebuilt there for real. 298
// intentionally ships no corpus UI rather than a placeholder grid.

export default function HomeOverview({ onAskBark, authToken, projectList, projectsLoading, projectsError, onRetryProjects, onProjectSelect, onNavigate }: HomeOverviewProps) {
    const [browseOpen, setBrowseOpen] = useState(false);
    // Live regulator feed. null until the first fetch resolves; falls back to
    // SEED_FEED when the endpoint returns nothing (cold cache / unverified
    // sources), so the section never renders empty during rollout.
    const [liveFeed, setLiveFeed] = useState<FeedItem[] | null>(() => loadCachedFeed());
    // Full filter vocabularies from the backend (stable over all items). Null
    // until the feed loads; we fall back to deriving from the current items.
    const [facets, setFacets] = useState<FeedFacets | null>(null);
    // "Ask BARK" creates a project (a backend round trip). One at a time:
    // the in-flight row shows a spinner and every row's trigger is disabled
    // (UX-STANDARD S-06), and a failure is shown inline rather than lost
    // to the console (S-05). Keyed by the row so only that row spins.
    const [askInFlight, setAskInFlight] = useState<string | null>(null);
    const [askError, setAskError] = useState<string | null>(null);
    // A research-less org still sees the feed; its "Ask BARK" must not be a
    // dead click (E-NAV-02) — it opens the shared Unlock dialog, like the
    // entry buttons and the sidebar.
    const { isModuleDisabled, usesChatFree } = useAuthActions();
    const { onOpenUnlockModule, navigateToView, beginBlankDraft } = useShellUiActions();
    const navigate = useNavigate();
    const researchLocked = isModuleDisabled('regulatory_research');
    // BAR-1528: with every web module off, the orchestrator refuses the feed
    // (403 `web_access_not_in_plan`). The fetch is skipped and the section says
    // so, instead of "Loading the latest regulatory updates…" that never ends.
    // A feed cached in this browser by an earlier session is not shown either.
    const webAccessOff = isChatLocked(isModuleDisabled, usesChatFree);
    const mcpOnly = isMcpOnlyCustomer(isModuleDisabled);
    // "Your work" is the returning user's first stop, so a build row here is a
    // live-looking door into a module the org may not hold — clicking it bounces
    // off the route gate and lands them back on this page with a dialog. Same
    // treatment as the project library: the row goes, and the one Locked entry
    // point above does the explaining.
    const buildLocked = isModuleDisabled('build_artifact');
    const visibleProjects = useMemo(
        () => (buildLocked ? projectList.filter((p) => p.project_type !== 'build') : projectList),
        [projectList, buildLocked],
    );

    // Same lock rules as the entry doors above (HomeEntryButtons): Chat locks
    // when every web module is off; a locked door opens the Unlock dialog, or
    // the connect page for an MCP-only customer.
    const openLocked = (label: string) => (mcpOnly ? navigateToView('connect') : onOpenUnlockModule(label));
    const openChat = () => (webAccessOff ? openLocked('Chat') : navigateToView('lookup'));
    const startProject = () => {
        if (!researchLocked) beginBlankDraft('research');
        else if (!buildLocked) beginBlankDraft('build');
        else openLocked('Regulatory research');
    };

    // Your chats: the newest few, from the same list as Chat's sidebar. A
    // locked Chat lists none (its turns are refused) and offers the locked door.
    const [chats, setChats] = useState<SavedLookupChat[] | null>(null);
    const [chatsError, setChatsError] = useState(false);
    // Bumped by "Try again" to load the chats once more.
    const [chatsAttempt, setChatsAttempt] = useState(0);
    useEffect(() => {
        // Every load starts clean: no error left over from an earlier one.
        setChatsError(false);
        // Without web access, or without a token, no chats can be listed.
        if (webAccessOff || !authToken) { setChats([]); return; }
        setChats(null);
        let cancelled = false;
        listLookupChats(authToken).then(
            (all) => { if (!cancelled) setChats(all.slice(0, HOME_LIST_SIZE)); },
            () => { if (!cancelled) setChatsError(true); },
        );
        return () => { cancelled = true; };
    }, [authToken, webAccessOff, chatsAttempt]);

    // Fetch the live feed once we have an auth token. Failures resolve to []
    // (api layer swallows errors) → we keep the seed.
    useEffect(() => {
        if (!authToken || webAccessOff) return;
        let cancelled = false;
        api.getRegulatoryFeed(authToken).then((result) => {
            if (cancelled) return;
            // Full filter vocabularies — keep even if the item list is empty.
            if (result.facets.typeFamilies.length) setFacets(result.facets);
            if (!result.items.length) return;  // keep last cached real feed
            const mapped: FeedItem[] = result.items.map((i) => ({
                source: i.source,
                date: i.date,
                type: i.type,
                title: i.title,
                summary: i.summary ?? undefined,
                link: i.link ?? undefined,
                regulationId: i.regulationId ?? undefined,
                categories: i.categories ?? [],
                question: i.question ?? undefined,
            }));
            setLiveFeed(mapped);
            saveCachedFeed(mapped);  // remember the last real feed for fallback
        });
        return () => { cancelled = true; };
    }, [authToken, webAccessOff]);

    const activeFeed = webAccessOff ? SEED_FEED : (liveFeed ?? SEED_FEED);

    // Feed filter state (persisted per-browser). "hidden" sets, default empty.
    const [hiddenSources, setHiddenSources] = useState<Set<string>>(() => loadHiddenSet(HIDDEN_SOURCES_KEY));
    const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(() => loadHiddenSet(HIDDEN_TYPES_KEY));
    const [hiddenCategories, setHiddenCategories] = useState<Set<string>>(() => loadHiddenSet(HIDDEN_CATEGORIES_KEY));

    const toggleHidden = (
        value: string,
        set: Set<string>,
        setter: React.Dispatch<React.SetStateAction<Set<string>>>,
        key: string,
    ) => {
        const next = new Set(set);
        if (next.has(value)) {
            next.delete(value);
        } else {
            next.add(value);
        }
        persistHiddenSet(key, next);
        setter(next);
    };

    const resetFilters = () => {
        const empty = new Set<string>();
        setHiddenSources(new Set(empty));
        setHiddenTypes(new Set(empty));
        setHiddenCategories(new Set(empty));
        persistHiddenSet(HIDDEN_SOURCES_KEY, empty);
        persistHiddenSet(HIDDEN_TYPES_KEY, empty);
        persistHiddenSet(HIDDEN_CATEGORIES_KEY, empty);
    };

    // Filter options = the backend's FULL stable vocabularies (the filter is a
    // persistent setting over all past/future items). Fall back to deriving
    // from the current items only when facets aren't available (seed/offline).
    const availableSources = useMemo(
        () => facets?.sources.length ? facets.sources : Array.from(new Set(activeFeed.map((i) => i.source))),
        [facets, activeFeed],
    );
    // Types filter on FAMILIES (cards keep the granular type). Build a
    // type→family lookup + the family-name list from the backend facets.
    const typeFamilyOf = useMemo(() => {
        const m = new Map<string, string>();
        (facets?.typeFamilies ?? []).forEach((f) => f.types.forEach((t) => m.set(t, f.name)));
        return m;
    }, [facets]);
    const availableTypes = useMemo(
        () => facets?.typeFamilies.length
            ? facets.typeFamilies.map((f) => f.name)
            : Array.from(new Set(activeFeed.map((i) => i.type))).sort(),
        [facets, activeFeed],
    );
    // "Tagging worked" = at least one item in the feed carries a real tag. That
    // tells us categorisation is genuinely live (not just flagged on but failing),
    // which is the condition under which empty items become "Other".
    const taggingWorked = useMemo(
        () => activeFeed.some((i) => (i.categories?.length ?? 0) > 0),
        [activeFeed],
    );
    const availableCategories = useMemo(() => {
        const base = facets?.categories.length
            ? facets.categories
            : Array.from(new Set(activeFeed.flatMap((i) => i.categories ?? []))).sort();
        // Offer "Other" as a toggle only when tagging actually worked, so the user
        // can switch off the no-theme-fit notices. When nothing is tagged there are
        // no categories at all and FeedFilter hides the column entirely.
        return taggingWorked ? [...base, OTHER_CATEGORY] : base;
    }, [facets, activeFeed, taggingWorked]);
    const filteredFeed = useMemo(
        () =>
            activeFeed.filter((i) => {
                const fam = typeFamilyOf.get(i.type) ?? i.type;
                if (hiddenSources.has(i.source) || hiddenTypes.has(fam)) return false;
                return passesCategoryFilter(effectiveCategories(i.categories ?? [], taggingWorked), hiddenCategories);
            }),
        [activeFeed, hiddenSources, hiddenTypes, hiddenCategories, typeFamilyOf, taggingWorked],
    );

    // Computed once per render — cheap and fine for a small feed.
    const today = new Date();
    const columnRows = withDividers(filteredFeed.slice(0, COLUMN_VISIBLE), today);
    const drawerRows = withDividers(filteredFeed, today);

    // BAR-298 (A): a feed item is awareness, not "ask" — clicking opens the
    // source publication. (The AI "Ask" path waits on an open-the-document
    // tool; until then we don't pretend BARK can answer about a doc it can't
    // read.) Seed items have no link → the card stays non-interactive.
    // BAR-538: "Ask BARK" per feed row — hands the item's enriched question
    // (or the RSS content itself: title/source/date/summary) to `onAskBark`,
    // which opens a research session with it as the first message. BARK
    // answers from its corpus plus this metadata; it does not open the
    // linked publication.
    // TRUST BOUNDARY: title/summary are external RSS strings. They travel as
    // plain chat text only (never innerHTML). If a future path renders the
    // seeded input as HTML/markdown, sanitize there — the feed side must
    // keep treating these fields as untrusted text.
    const feedItemKey = (item: FeedItem) => `${item.source}|${item.date}|${item.title}`;

    const askBark = (item: FeedItem) => {
        if (researchLocked) {
            onOpenUnlockModule('Regulatory research');
            return;
        }
        if (askInFlight !== null) return;
        // Injection mitigation: RSS fields are quoted inside a delimited
        // block with the user's actual question OUTSIDE it, so the model
        // reads them as cited material, not instructions. Newlines inside
        // the fields are collapsed so the delimiters stay unambiguous.
        const line = (v: string) => v.replace(/\s+/g, ' ').trim();
        const seed = (item.question && item.question.trim())
            || [
                'What does this publication mean for us?',
                '---',
                `Title: ${line(item.title)}`,
                `Source: ${line(item.source)}, ${item.date}`,
                ...(item.summary ? [`Summary: ${line(item.summary)}`] : []),
                '---',
            ].join('\n');
        setAskError(null);
        setAskInFlight(feedItemKey(item));
        // On success the shell navigates to the new project and this view
        // unmounts; on failure the row comes back with an inline message.
        void Promise.resolve(onAskBark(seed))
            .catch((err) => {
                console.error('[HomeOverview] Ask BARK failed:', err);
                setAskError('Failed to create project. Please try again.');
            })
            .finally(() => setAskInFlight(null));
    };

    const openSource = (item: FeedItem) => {
        if (!item.link) return;
        // Only open real web links — guard against javascript:/data: and other
        // schemes sneaking through a feed item (open-redirect / XSS vector).
        let safe = false;
        try {
            safe = ['http:', 'https:'].includes(new URL(item.link).protocol);
        } catch {
            safe = false;
        }
        if (safe) {
            window.open(item.link, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <div className="w-full">
            {/* BAR-538 phase 1: vertical stack — "Your work", then the module
                cards (Research, Build), then the two-column content grid
                (feed | Regulatory-monitoring preview). The composer that used
                to sit on top is gone; the Research / Build entry buttons in
                LandingPage take its place. */}
            {/* Your chats (left) and Your projects (right), the returning
                user's first stop. Each shows its 5 newest; the section never
                hides, and an empty list offers a button that opens a place to
                start typing (a new chat, a blank research project). */}
            <div className="mt-6 lg:mt-8 mb-10 lg:mb-14 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:items-start">
                <section aria-label="Your chats">
                    <div className="flex items-baseline justify-between mb-3 px-1">
                        <h2 className="font-body text-[20px] font-semibold text-fg leading-tight">
                            Your chats
                        </h2>
                        <button
                            type="button"
                            onClick={openChat}
                            className="group font-mono text-[11px] uppercase tracking-[0.08em] font-semibold text-fg hover:underline underline-offset-2 transition-colors flex items-center gap-1.5"
                        >
                            <span>Open chat</span>
                            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />
                        </button>
                    </div>
                    <div className="flex flex-col border-t border-divider">
                        {chats === null && !chatsError && (
                            <p role="status" className="flex items-center gap-2 px-5 py-3 text-[13px] text-fg-secondary">
                                <Loader2 className="w-4 h-4 animate-spin motion-reduce:animate-none" strokeWidth={1.5} aria-hidden="true" />
                                Loading chats
                            </p>
                        )}
                        {chatsError && (
                            <div role="alert" className="mx-5 my-3 bg-danger-bg rounded-md p-3 text-sm">
                                <p>Your chats could not be loaded.</p>
                                <button type="button" className="mt-2 underline rounded focus-visible:shadow-bark-focus" onClick={() => setChatsAttempt((n) => n + 1)}>Try again</button>
                            </div>
                        )}
                        {chats?.length === 0 && (
                            <div className="px-5 py-8 flex flex-col items-center gap-1 text-center">
                                <button type="button" onClick={openChat} className={emptyStartClass}>
                                    <span aria-hidden="true" className={emptyStartCircle}><Plus className="w-5 h-5" strokeWidth={1.5} /></span>
                                    Start new chat
                                </button>
                                <p className="text-[13px] text-fg-secondary">Your quick questions show up here.</p>
                            </div>
                        )}
                        {chats?.map((chat) => (
                            <button
                                key={chat.chat_id}
                                type="button"
                                onClick={() => navigate(`/lookup/${encodeURIComponent(chat.chat_id)}`)}
                                className="group w-full text-left px-5 py-3 hover:bg-surface-sunken transition-colors border-b border-divider grid items-center gap-3"
                                style={{ gridTemplateColumns: '16px minmax(0,1fr)' }}
                            >
                                <MessageSquare className="w-4 h-4 text-fg-tertiary" strokeWidth={1.5} aria-hidden="true" />
                                <div className="min-w-0 flex flex-col gap-0.5">
                                    <div className="font-body text-[13px] font-semibold text-fg truncate group-hover:text-primary transition-colors">
                                        {chat.title}
                                    </div>
                                    <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-fg-tertiary">
                                        <span>chat</span>
                                        <span className="opacity-50">·</span>
                                        <span>{relativeTime(chat.updated_at, today)}</span>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </section>

                <section aria-label="Your projects">
                    <div className="flex items-baseline justify-between mb-3 px-1">
                        <h2 className="font-body text-[20px] font-semibold text-fg leading-tight">
                            Your projects
                        </h2>
                        <button
                            type="button"
                            onClick={() => onNavigate('projects')}
                            className="group font-mono text-[11px] uppercase tracking-[0.08em] font-semibold text-fg hover:underline underline-offset-2 transition-colors flex items-center gap-1.5"
                        >
                            <span>View your projects</span>
                            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />
                        </button>
                    </div>
                    <div className="flex flex-col border-t border-divider">
                        {visibleProjects.length === 0 && projectsError && !projectsLoading && (
                            <div role="alert" className="mx-5 my-3 bg-danger-bg rounded-md p-3 text-sm">
                                <p>Your projects could not be loaded.</p>
                                {onRetryProjects && (
                                    <button type="button" className="mt-2 underline rounded focus-visible:shadow-bark-focus" onClick={onRetryProjects}>Try again</button>
                                )}
                            </div>
                        )}
                        {visibleProjects.length === 0 && projectsLoading && (
                            <p role="status" className="flex items-center gap-2 px-5 py-3 text-[13px] text-fg-secondary">
                                <Loader2 className="w-4 h-4 animate-spin motion-reduce:animate-none" strokeWidth={1.5} aria-hidden="true" />
                                Loading projects
                            </p>
                        )}
                        {visibleProjects.length === 0 && !projectsLoading && !projectsError && (
                            <div className="px-5 py-8 flex flex-col items-center gap-1 text-center">
                                <button type="button" onClick={startProject} className={emptyStartClass}>
                                    <span aria-hidden="true" className={emptyStartCircle}><Plus className="w-5 h-5" strokeWidth={1.5} /></span>
                                    Start a project
                                </button>
                                <p className="text-[13px] text-fg-secondary">Your research and build projects show up here.</p>
                            </div>
                        )}
                        {[...visibleProjects]
                            .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
                            .slice(0, HOME_LIST_SIZE)
                            .map((p) => {
                                const ptype: 'research' | 'build' = p.project_type === 'build' ? 'build' : 'research';
                                const accent = ptype === 'build' ? 'bg-accent-copper' : 'bg-secondary';
                                const Icon = ptype === 'build' ? FolderKanban : Search;
                                return (
                                    <button
                                        key={p.project_id}
                                        type="button"
                                        onClick={() => onProjectSelect(p.project_id)}
                                        className="group w-full text-left px-5 py-3 hover:bg-surface-sunken transition-colors border-b border-divider grid items-center gap-3"
                                        style={{ gridTemplateColumns: '3px minmax(0,1fr)' }}
                                    >
                                        <span aria-hidden="true" className={`h-8 w-[3px] rounded-sm ${accent}`} />
                                        <div className="min-w-0 flex flex-col gap-0.5">
                                            <div className="font-body text-[13px] font-semibold text-fg truncate group-hover:text-primary transition-colors">
                                                {p.name || 'Untitled project'}
                                            </div>
                                            <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-fg-tertiary">
                                                <Icon className="w-3 h-3" strokeWidth={1.5} />
                                                <span>{ptype}</span>
                                                <span className="opacity-50">·</span>
                                                <span>{relativeTime(p.updated_at, today)}</span>
                                                {p.has_report && (
                                                    <>
                                                        <span className="opacity-50">·</span>
                                                        <FileText className="w-3 h-3" strokeWidth={1.5} />
                                                        <span>report ready</span>
                                                    </>
                                                )}
                                                {p.workspace_validated && (
                                                    <>
                                                        <span className="opacity-50">·</span>
                                                        <Check className="w-3 h-3 text-success" strokeWidth={2.5} />
                                                        <span className="text-success">validated</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                    </div>
                </section>
            </div>

            {/* Between your work and the news: what else BARK offers (Reporting,
                Inventory and two "soon" cards). The Research / Build how-to
                cards stay off the start page; the entry buttons lead to both. */}
            <div aria-hidden="true" className="mx-auto h-0.5 w-40 rounded-full bg-border-default mb-10 lg:mb-14" />

            <HomeModuleCards tutorials={false} />

            {/* Two columns: regulator scan left, Regulatory-monitoring
                preview right (BAR-538; the module ships via BAR-294). */}
            <div aria-hidden="true" className="mx-auto h-0.5 w-40 rounded-full bg-border-default mt-10 lg:mt-14" />

            <div className="mt-10 lg:mt-14 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 lg:items-start">
                <div className="flex flex-col gap-6 lg:gap-8">
                    <div className="flex-1 flex flex-col">
                    <div className="flex items-baseline justify-between mb-3 px-5">
                        <span className="font-body text-[20px] font-semibold text-fg leading-tight">
                            Latest from the regulators
                        </span>
                        {!webAccessOff && (
                            <div className="flex items-center gap-4">
                                <FeedFilter
                                    sources={availableSources}
                                    types={availableTypes}
                                    categories={availableCategories}
                                    hiddenSources={hiddenSources}
                                    hiddenTypes={hiddenTypes}
                                    hiddenCategories={hiddenCategories}
                                    onToggleSource={(s) => toggleHidden(s, hiddenSources, setHiddenSources, HIDDEN_SOURCES_KEY)}
                                    onToggleType={(t) => toggleHidden(t, hiddenTypes, setHiddenTypes, HIDDEN_TYPES_KEY)}
                                    onToggleCategory={(c) => toggleHidden(c, hiddenCategories, setHiddenCategories, HIDDEN_CATEGORIES_KEY)}
                                    onReset={resetFilters}
                                />
                            </div>
                        )}
                    </div>
                    {askError && (
                        <div role="alert" className="mx-5 mb-3 rounded-md bg-danger-bg px-3 py-2 font-body text-[12px] text-danger">
                            {askError}
                        </div>
                    )}
                    <div className="flex flex-col border-t border-divider">
                        {filteredFeed.length === 0 && (webAccessOff ? (
                            <div className="px-5 py-8 font-body text-[13px] text-fg-tertiary flex items-start gap-2">
                                <Lock className="w-4 h-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                                <span>
                                    {mcpOnly
                                        ? 'Regulator updates are part of the BARK web app, which is not in your plan.'
                                        : 'Regulator updates are not enabled for your organization.'}
                                </span>
                            </div>
                        ) : (
                            <div className="px-5 py-8 font-body text-[13px] text-fg-tertiary">
                                Loading the latest regulatory updates…
                            </div>
                        ))}
                        {columnRows.map((row, idx) => row.kind === 'divider' ? (
                            <div
                                key={`col-d-${idx}`}
                                className="px-5 py-1.5 bg-neutral-warm font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-fg-tertiary border-b border-divider"
                            >
                                {row.label}
                            </div>
                        ) : (
                            <div
                                // A real <button> cannot be used here: the row
                                // contains a nested interactive control (Ask
                                // BARK), and button-in-button is invalid HTML.
                                // Enter+Space activation and a visible focus
                                // ring are provided manually instead.
                                key={`col-i-${row.item.source}-${row.item.date}-${idx}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => openSource(row.item)}
                                onKeyDown={(e) => {
                                    // role="button" must activate on Enter AND Space;
                                    // preventDefault stops Space from scrolling the page.
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        openSource(row.item);
                                    }
                                }}
                                className="group w-full cursor-pointer text-left px-5 py-3.5 hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-inset transition-colors border-b border-divider last:border-b-0 grid items-center gap-3"
                                style={{ gridTemplateColumns: '3px minmax(0,1fr)' }}
                            >
                                <span aria-hidden="true" className="h-12 w-[3px] rounded-sm bg-primary" />
                                <div className="flex flex-col gap-1 min-w-0">
                                    <div className="flex items-center justify-between gap-3 font-mono text-[11px] uppercase tracking-[0.08em] text-fg-tertiary">
                                        <span className="inline-flex items-center gap-2 min-w-0">
                                            <span className="font-semibold text-fg-secondary">{row.item.source}</span>
                                            <span className="opacity-50">·</span>
                                            <span>{row.item.date}</span>
                                            <span className="opacity-50">·</span>
                                            <span className="truncate">{row.item.type}</span>
                                            {effectiveCategories(row.item.categories ?? [], taggingWorked).length > 0 && (
                                                <>
                                                    <span className="opacity-50">·</span>
                                                    <span className="truncate normal-case tracking-normal text-fg-secondary">
                                                        {effectiveCategories(row.item.categories ?? [], taggingWorked).join(', ')}
                                                    </span>
                                                </>
                                            )}
                                        </span>
                                        {row.item.regulationId && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded font-mono text-[11px] font-medium normal-case tracking-normal bg-link-bg text-link shrink-0">
                                                {row.item.regulationId}
                                            </span>
                                        )}
                                    </div>
                                    <div className="font-body text-[13px] font-semibold text-fg leading-tight truncate">
                                        {row.item.title}
                                    </div>
                                    {/* Actions always visible (owner 2026-07-13); summary line removed */}
                                    <div className="flex items-center gap-4 mt-0.5">
                                        <button
                                            type="button"
                                            disabled={askInFlight !== null}
                                            onClick={(e) => { e.stopPropagation(); askBark(row.item); }}
                                            title={researchLocked ? 'Not enabled for your organization' : undefined}
                                            className={`font-mono text-[11px] font-semibold uppercase tracking-[0.08em] transition-colors inline-flex items-center gap-1.5
                                                        disabled:opacity-60 disabled:cursor-not-allowed ${
                                                researchLocked
                                                    ? 'text-fg-tertiary hover:text-fg-secondary'
                                                    : 'text-secondary hover:text-primary'
                                            }`}
                                        >
                                            {askInFlight === feedItemKey(row.item) ? (
                                                <>
                                                    <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2} aria-hidden="true" />
                                                    Creating project…
                                                </>
                                            ) : (
                                                <>
                                                    {researchLocked && <Lock className="w-3 h-3" strokeWidth={2} aria-hidden="true" />}
                                                    Ask BARK
                                                    {researchLocked && <span className="sr-only"> — not enabled for your organization</span>}
                                                </>
                                            )}
                                        </button>
                                        {row.item.link && (
                                            <span
                                                aria-hidden="true"
                                                className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-fg-tertiary group-hover:text-primary transition-colors"
                                            >
                                                View source &rarr;
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {/* Nothing to browse without the feed; no dead door. */}
                    {!webAccessOff && (
                        <button
                            type="button"
                            onClick={() => setBrowseOpen(true)}
                            className="group mt-auto px-5 py-3 border-t border-divider text-right font-mono text-[11px] uppercase tracking-[0.08em] font-semibold text-fg hover:underline underline-offset-2 transition-colors flex items-center justify-end gap-1.5"
                        >
                            <span>View recent</span>
                            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />
                        </button>
                    )}
                </div>

                </div>

                {/* RIGHT — Regulatory monitoring preview: how the module will
                    look. Rows are labelled Example — the no-fabricated-
                    publications rule applies (see SEED_FEED note above). */}
                <aside className="flex flex-col min-h-0 lg:order-first">
                    <div className="flex items-baseline justify-between mb-3 px-5">
                        <span className="font-body text-[20px] font-semibold text-fg-tertiary leading-tight flex items-center gap-2">
                            Regulatory monitoring
                            <span className="text-[9px] font-semibold bg-bg-warm text-fg-tertiary px-1 py-px rounded-full uppercase tracking-wider">soon</span>
                        </span>
                    </div>
                    <div className="flex flex-col border-t border-divider">
                        {[
                            { tag: 'Example', text: 'A new guideline is published. BARK flags the 3 documents it touches.' },
                            { tag: 'Example', text: 'An article you rely on is amended. Your affected models are listed.' },
                            { tag: 'Example', text: 'Nothing relevant this week. The feed stays quiet.' },
                        ].map((row, i) => (
                            <div
                                key={`monitor-ex-${i}`}
                                className="px-5 py-3.5 border-b border-divider grid items-center gap-3 opacity-70"
                                style={{ gridTemplateColumns: '3px minmax(0,1fr)' }}
                            >
                                <span aria-hidden="true" className="h-8 w-[3px] rounded-sm bg-border-strong" />
                                <div className="min-w-0 flex flex-col gap-0.5">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-fg-tertiary">{row.tag}</div>
                                    <div className="font-body text-[13px] text-fg-secondary leading-snug">{row.text}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <p className="px-5 pt-3 font-body text-[12px] leading-relaxed text-fg-tertiary">
                        BARK reads new regulation and flags which of your documents and models it touches.
                    </p>
                </aside>

            </div>

            {/* "In the corpus" section removed — corpus is BAR-301 (UI + pipeline). */}

            {/* Browse-all drawer */}
            {browseOpen && (
              <DialogShell
                open={browseOpen}
                onClose={() => setBrowseOpen(false)}
                ariaLabel="Latest from the regulators"
                backdropPolicy="dismiss"
                overlayClassName="flex justify-end p-4"
                panelClassName="h-full w-[520px] max-w-[calc(100vw-32px)] border border-border-default flex flex-col overflow-hidden"
            >
                        <div className="flex items-baseline justify-between px-6 pt-5 pb-3 border-b border-divider">
                            <h2 className="font-heading text-[24px] font-normal leading-tight tracking-[-0.02em] text-fg">
                                Latest from the regulators
                            </h2>
                            <button
                                type="button"
                                onClick={() => setBrowseOpen(false)}
                                className="font-mono text-[11px] uppercase tracking-wider text-fg-tertiary hover:text-primary transition-colors px-2 py-1 rounded"
                            >
                                Esc to close
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {drawerRows.map((row, idx) => row.kind === 'divider' ? (
                                <div
                                    key={`drawer-d-${idx}`}
                                    className="px-6 py-1.5 bg-neutral-warm font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-fg-tertiary border-b border-divider"
                                >
                                    {row.label}
                                </div>
                            ) : (
                                <button
                                    key={`drawer-i-${row.item.source}-${row.item.date}-${idx}`}
                                    type="button"
                                    onClick={() => openSource(row.item)}
                                    className="group w-full text-left px-6 py-3.5 hover:bg-surface-sunken transition-colors border-b border-divider last:border-b-0 grid items-center gap-3"
                                    style={{ gridTemplateColumns: '3px minmax(0,1fr)' }}
                                >
                                    <span aria-hidden="true" className="h-12 w-[3px] rounded-sm bg-primary" />
                                    <div className="flex flex-col gap-1 min-w-0">
                                        <div className="flex items-center justify-between gap-3 font-mono text-[11px] uppercase tracking-[0.08em] text-fg-tertiary">
                                            <span className="inline-flex items-center gap-2 min-w-0">
                                                <span className="font-semibold text-fg-secondary">{row.item.source}</span>
                                                <span className="opacity-50">·</span>
                                                <span>{row.item.date}</span>
                                                <span className="opacity-50">·</span>
                                                <span className="truncate">{row.item.type}</span>
                                            {effectiveCategories(row.item.categories ?? [], taggingWorked).length > 0 && (
                                                <>
                                                    <span className="opacity-50">·</span>
                                                    <span className="truncate normal-case tracking-normal text-fg-secondary">
                                                        {effectiveCategories(row.item.categories ?? [], taggingWorked).join(', ')}
                                                    </span>
                                                </>
                                            )}
                                            </span>
                                            {row.item.regulationId && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded font-mono text-[11px] font-medium normal-case tracking-normal bg-link-bg text-link shrink-0">
                                                    {row.item.regulationId}
                                                </span>
                                            )}
                                        </div>
                                        <div className="font-body text-[15px] font-semibold text-fg leading-tight">
                                            {row.item.title}
                                        </div>
                                        {row.item.link && (
                                            <div className="flex items-center gap-4 mt-0.5">
                                                <span
                                                    aria-hidden="true"
                                                    className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-fg-tertiary group-hover:text-primary transition-colors"
                                                >
                                                    View source &rarr;
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
              </DialogShell>
            )}
        </div>
    );
}
